
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useIDTypes } from "@/hooks/admin/identity-docs/useIDTypes";
import { toast } from "sonner";
import type { IDDocumentType } from "@/hooks/admin/identity-docs/useIDTypes";

interface Country {
  id: string;
  code: string;
  name: string;
}

interface IDTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  countries: Country[];
  idType?: IDDocumentType; // Pass idType for editing
}

export function IDTypeFormDialog({ open, onOpenChange, countries, idType }: IDTypeFormDialogProps) {
  const { createIDType, updateIDType } = useIDTypes();
  const [formData, setFormData] = useState({
    document_name: "",
    country_code: "",
    description: "",
    required: false,
    global_document: false,
    expatriate_identifier: false,
    proves_citizenship: false,
    proves_residency: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when editing an existing ID type
  useEffect(() => {
    if (idType) {
      setFormData({
        document_name: idType.document_name || "",
        country_code: idType.country_code || "",
        description: idType.description || "",
        required: idType.required || false,
        global_document: idType.global_document || false,
        expatriate_identifier: idType.expatriate_identifier || false,
        proves_citizenship: idType.proves_citizenship || false,
        proves_residency: idType.proves_residency || false
      });
    } else {
      // Reset form when adding new
      setFormData({
        document_name: "",
        country_code: "",
        description: "",
        required: false,
        global_document: false,
        expatriate_identifier: false,
        proves_citizenship: false,
        proves_residency: false
      });
    }
  }, [idType, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (idType) {
        // Update existing ID type
        await updateIDType.mutateAsync({
          id: idType.id,
          ...formData
        });
        toast.success("ID type updated successfully");
      } else {
        // Create new ID type
        await createIDType.mutateAsync(formData);
        toast.success("ID type created successfully");
      }
      
      setFormData({
        document_name: "",
        country_code: "",
        description: "",
        required: false,
        global_document: false,
        expatriate_identifier: false,
        proves_citizenship: false,
        proves_residency: false
      });
      onOpenChange(false);
    } catch (error: any) {
      toast.error(idType ? "Error updating ID type" : "Error creating ID type");
      console.error("Error with ID type:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{idType ? "Edit ID Document Type" : "Add ID Document Type"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="document_name">Document Name</Label>
              <Input 
                id="document_name"
                value={formData.document_name}
                onChange={(e) => setFormData({...formData, document_name: e.target.value})}
                placeholder="e.g. National ID Card"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="country">Country</Label>
              <Select 
                value={formData.country_code} 
                onValueChange={(value) => setFormData({...formData, country_code: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.id} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea 
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Provide details about this document type"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch 
                  id="required"
                  checked={formData.required}
                  onCheckedChange={(checked) => setFormData({...formData, required: checked})}
                />
                <Label htmlFor="required">Required for verification</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch 
                  id="global_document"
                  checked={formData.global_document}
                  onCheckedChange={(checked) => setFormData({...formData, global_document: checked})}
                />
                <Label htmlFor="global_document">Global Document</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch 
                  id="expatriate_identifier"
                  checked={formData.expatriate_identifier}
                  onCheckedChange={(checked) => setFormData({...formData, expatriate_identifier: checked})}
                />
                <Label htmlFor="expatriate_identifier">Expatriate Identifier</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch 
                  id="proves_citizenship"
                  checked={formData.proves_citizenship}
                  onCheckedChange={(checked) => setFormData({...formData, proves_citizenship: checked})}
                />
                <Label htmlFor="proves_citizenship">Proves Citizenship</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch 
                  id="proves_residency"
                  checked={formData.proves_residency}
                  onCheckedChange={(checked) => setFormData({...formData, proves_residency: checked})}
                />
                <Label htmlFor="proves_residency">Proves Residency</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formData.document_name || !formData.country_code || isSubmitting}>
              {isSubmitting ? (idType ? 'Updating...' : 'Saving...') : (idType ? 'Update' : 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
